import React, { useEffect, useCallback, useContext, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import ErrorBoundary from 'components/Utils/ErrorBoundary';
import TimeBar from 'components/Patient/CPP/TimeBar';
import CPPInput from 'components/Patient/CPP/CPPInput';
import CPPTimeline from 'components/Patient/CPP/CPPTimeline/index';
import CPPHealthRecordsPanel from 'components/Patient/CPP/CPPHealthRecordsPanel';
import './style.scss';
import RouteUtils from 'services/RouteUtils';
import Icon from 'components/Utils/NewIcon';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { usePathParams } from 'components/Navigation/NavStack';
import { useNavStack } from 'careand-redux/slices/ui/hooks';
import { Stacks } from 'careand-redux/slices/ui/navigation';
import { NoteList } from 'components/HealthRecords/AppointmentNotes/AppointmentNoteScreen';
import ResponsiveColumns from 'components/ResponsiveColumns';
import { Device } from 'services/Native/Device';
import { useMediaQuery } from '@material-ui/core';

// Sections that render as inline input areas (same space as the note) — mutually exclusive with note
const INLINE_SECTIONS = new Set([
  'prescription',
  'referral',
  'documents',
  'favorites',
  'immunizations',
  'administered_drugs',
]);

const CPP = (props) => {
  const {
    patient,
    bubble,
    search,
    setSearch,
    showTimeBar,
    queueElement,
    defaultHidePanel = false,
  } = props;

  const params = usePathParams();
  const activeTab = params.tab;
  const patientId = patient.id;

  const nav = useNavStack(Stacks.Master);
  const [openList, setOpenList] = useState(false);
  const dispatch = useAppDispatch();
  const role = useAppSelector((state) => state.authentication.role);

  const isMobile = Device.isMobile();
  const isSmallScreen = useMediaQuery('(max-width: 767px)');
  const isPhone = isMobile || isSmallScreen;

  const [hidePanel, setHidePanel] = useState(defaultHidePanel);
  const [activeCPPSections, setActiveCPPSections] = useState(new Set());
  const cppTopRef = useRef(null);
  const scrollTimelineToTop = useCallback(() => {
    cppTopRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const closeCPPSection = useCallback((sectionId) => {
    setActiveCPPSections((prev) => {
      const next = new Set(prev);
      next.delete(sectionId);
      return next;
    });
  }, []);

  const setActiveCPPTab = useCallback(
    (elementId) => {
      if (elementId === 'note') {
        setActiveCPPSections(new Set());
        let route;
        if (role === 'admin') {
          route = RouteUtils.getRoute(RouteUtils.routes().patient, {
            ':patientId': patientId,
            ':tab?': 'timeline',
            ':elementId?': elementId,
          });
        } else {
          route = RouteUtils.getCPPRoute(patientId, elementId, bubble);
        }
        route = route.split('//').join('/');
        nav.push(route);
      } else {
        setActiveCPPSections(new Set([elementId]));
        // Inline sections (not modals) occupy the same space as the note — close it
        if (INLINE_SECTIONS.has(elementId)) {
          const noteIsOpen = activeTab === 'note' || params.elementId === 'note';
          if (noteIsOpen) {
            let route;
            if (role === 'admin') {
              route = RouteUtils.getRoute(RouteUtils.routes().patient, {
                ':patientId': patientId,
                ':tab?': 'timeline',
                ':elementId?': undefined,
              });
            } else {
              route = RouteUtils.getCPPRoute(patientId, undefined, bubble);
            }
            route = route.split('//').join('/');
            nav.push(route);
          }
        }
      }
    },
    [patientId, role, bubble, nav, activeTab, params.elementId],
  );

  useEffect(() => {
    dispatch(api.HealthRecords.getCPP({ patientId })).then(() => {
      dispatch(api.Resource.getCalls({ patientId }));
      dispatch(api.Reminders.getReminders({ userId: patientId }));
      dispatch(api.Resource.getPatientCommunicationNotes({ id: patientId }));
      dispatch(api.HealthRecords.getHealthDataSummary({ patientId }));
      dispatch(api.HealthRecords.getPatientLMP({ id: patientId }));
      dispatch(api.Fax.getPatientFaxes({ patientId }));
      if (role === 'physician') dispatch(api.Tasks.getPatientTasks({ patientId }));
    });
    if (role === 'physician') dispatch(api.HealthRecords.getPatientNotesDraft({ patientId }));
  }, [dispatch, patientId, role]);

  function toggleHiddenPanel() {
    setHidePanel(!hidePanel);
  }

  const cppClassName = useMemo(() => {
    let cppClassName = 'row cpp';
    if (showTimeBar) cppClassName += ' padding-top';
    if (hidePanel) cppClassName += ' hidden-bar';
    return cppClassName;
  }, [showTimeBar, hidePanel]);

  const leftBarClassName = useMemo(() => {
    let leftBarClassName = 'left-bar';
    if (hidePanel) leftBarClassName += ' hide';
    return leftBarClassName;
  }, [hidePanel]);

  return (
    <div className="cpp-wrapper" key={patientId}>
      {!!props.showTimeBar && (
        <TimeBar
          patient={patient}
          bubble={bubble}
          onArchiveChat={props.onArchiveChat}
          queueElement={queueElement}
          showNavButtons={!isMobile}
          hidePanel={hidePanel}
          onToggleHidePanel={toggleHiddenPanel}
          openList={openList}
          onToggleOpenList={() => setOpenList(!openList)}
          onScrollToTop={scrollTimelineToTop}
        />
      )}
      <div className={cppClassName}>
        <ResponsiveColumns
          defaultTab={isPhone ? 2 : 1}
          columns={[
            {
              name: 'Health Records',
              element: (
                <div className={leftBarClassName}>
                  {!hidePanel && (
                    <CPPHealthRecordsPanel
                      patient={patient}
                      bubble={bubble}
                      setActiveCPPTab={setActiveCPPTab}
                      onSearch={setSearch}
                    />
                  )}
                </div>
              ),
            },
            isPhone
              ? {
                  name: 'Notes',
                  element: <NoteList patient={props.patient} />,
                }
              : undefined,
            {
              name: 'Timeline',
              element: (
                <div className="cpp-center-column-wrapper">
                  {openList && <NoteList patient={props.patient} />}
                  <div className="cpp-center-column">
                    <ErrorBoundary>
                      {activeTab !== 'note' ? (
                        <CPPTimeline
                          patient={patient}
                          bubble={bubble}
                          search={search}
                          setActiveCPPTab={setActiveCPPTab}
                          cppTopRef={cppTopRef}
                        />
                      ) : null}
                    </ErrorBoundary>
                    <ErrorBoundary>
                      <CPPInput
                        patient={patient}
                        bubble={bubble}
                        activeCPPSections={activeCPPSections}
                        setActiveCPPTab={setActiveCPPTab}
                        closeCPPSection={closeCPPSection}
                      />
                    </ErrorBoundary>
                  </div>
                </div>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
};

CPP.propTypes = {
  patient: PropTypes.object.isRequired,
  bubble: PropTypes.object,
  showTimeBar: PropTypes.bool,
  search: PropTypes.string,
  onArchiveChat: PropTypes.func,
};

export default CPP;
